import { Fragment } from 'react';

import { Document, Font, Page, View } from '@react-pdf/renderer';
import { HyphenationCallback } from '@react-pdf/types';
import { DateTime } from 'luxon';

import { commonStyles } from 'src/common/pdf/common.styles';

import { InvoiceColumnKey } from '../../modals/DownloadInvoices/steps/components/InvoiceColumnSelect';
import {
  InvoiceExportCondensedReportItem,
  InvoicePaymentRow,
  InvoiceRefundRow,
} from '../../types';
import { ExpandedPaymentsLine } from '../components/payments/ExpandedPaymentsLine';
import { ExpandedRefundsLine } from '../components/refunds/ExpandedRefundsLine';
import { InvoicePDFFooter } from '../invoice/InvoicePDFFooter';
import { InvoicePDFHeader } from '../invoice/InvoicePDFHeader';
import { InvoicePDFOrganizationHeader } from '../invoice/InvoicePDFOrganizationHero';
import { InvoicePDFPriceSummary } from '../invoice/InvoicePDFPriceSummary';
import { InvoicePDFStats } from '../invoice/InvoicePDFStats';
import { InvoicePDFTripTable } from '../invoice/InvoicePDFTripTable';
import { useInvoiceData } from '../useInvoiceData';

import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

// Support for emojis, see: https://react-pdf.org/fonts
Font.registerEmojiSource({
  format: 'png',
  url: 'https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/',
});

// Avoid hyphenations
const hyphenationCallback: HyphenationCallback = (word) => [word];
Font.registerHyphenationCallback(hyphenationCallback);

interface ReceiptPDFDocumentProps {
  organizationBillingAddress: string;
  organizationBillingEmail: string;
  organizationBillingPhone: string;
  organizationName: string;
  organizationLogo: string;
  organizationTaxId?: string;
  invoiceNote: string;
  invoiceItems: InvoiceExportCondensedReportItem[];
  includePriceSummary?: boolean;
  selectedInvoiceColumns: SelectOption<InvoiceColumnKey>[];
  invoicePayments: InvoicePaymentRow[];
  invoiceRefunds: InvoiceRefundRow[];
}

export function ReceiptPDFDocument({
  organizationBillingAddress,
  organizationBillingEmail,
  organizationBillingPhone,
  organizationLogo,
  organizationName,
  organizationTaxId,
  invoiceItems,
  invoiceNote,
  includePriceSummary = false,
  selectedInvoiceColumns,
  invoicePayments,
  invoiceRefunds,
}: ReceiptPDFDocumentProps) {
  const updatedInvoiceItems = useInvoiceData(
    invoiceItems,
    invoicePayments,
    invoiceRefunds
  );

  return (
    <Document>
      {updatedInvoiceItems.map((ii) => {
        return (
          <Fragment key={ii.item.InvoiceNo}>
            <Page size="A4" orientation="landscape">
              <View style={[commonStyles.page, { paddingTop: 0 }]}>
                <InvoicePDFHeader
                  invoiceNumber={ii.item.InvoiceNo}
                  issueDate={ii.invoiceDate.toLocaleString(DateTime.DATE_SHORT)}
                  amountDue={ii.invoiceDueAmount}
                  billingAddress={organizationBillingAddress}
                  billingEmail={organizationBillingEmail}
                  billingPhone={organizationBillingPhone}
                  organizationName={organizationName}
                  logo={organizationLogo}
                  generateAsReceipt={!!ii.paymentsTotalAmount}
                />
                <InvoicePDFOrganizationHeader
                  organizationName={organizationName}
                  invoiceNote={invoiceNote}
                />
                <InvoicePDFStats
                  amountDue={ii.invoiceDueAmount}
                  dueDate={ii.invoiceDueDate}
                  miles={ii.totalMiles}
                  passengerCount={ii.totalUniqPassengers}
                  payerAddress={ii.item.ItemPayerBillingAddress}
                  payerName={ii.item.ItemPayerName}
                  payerContactName={ii.item.ItemPayerBillingContactName}
                  payerPhone={ii.item.ItemPayerBillingContactPhone}
                  tripCount={ii.totalTrips}
                  organizationTaxId={organizationTaxId}
                />
                <InvoicePDFTripTable
                  rows={ii.item.items}
                  selectedInvoiceColumns={selectedInvoiceColumns}
                />
                <InvoicePDFFooter
                  adjustmentAmount={ii.modifiersTotalAmount}
                  invoiceNotes={ii.item.InvoiceNotes}
                  subTotalAmount={ii.tripsTotalAmount}
                  totalAmount={ii.invoiceDueAmount}
                  paymentLine={
                    <ExpandedPaymentsLine
                      payments={ii.payments}
                      paymentAmount={ii.paymentsTotalAmount}
                    />
                  }
                  refundLine={
                    ii.refunds.length ? (
                      <ExpandedRefundsLine
                        refunds={ii.refunds}
                        refundAmount={ii.refundsTotalAmount}
                      />
                    ) : null
                  }
                />
              </View>
            </Page>
            {includePriceSummary &&
            ii.priceSummaryCompatibleLineItems.length ? (
              <Page size="A4" orientation="landscape">
                <View style={[commonStyles.page, { paddingTop: 0 }]}>
                  <InvoicePDFHeader
                    invoiceNumber={ii.item.InvoiceNo}
                    issueDate={ii.invoiceDate.toLocaleString(
                      DateTime.DATE_SHORT
                    )}
                    amountDue={ii.invoiceDueAmount}
                    billingAddress={organizationBillingAddress}
                    billingEmail={organizationBillingEmail}
                    billingPhone={organizationBillingPhone}
                    organizationName={organizationName}
                    logo={organizationLogo}
                  />
                  <InvoicePDFPriceSummary
                    invoiceNumber={ii.item.InvoiceNo}
                    items={ii.priceSummaryCompatibleLineItems}
                  />
                </View>
              </Page>
            ) : null}
          </Fragment>
        );
      })}
    </Document>
  );
}
